import dayjs from "dayjs";
import { useContext, useEffect } from "react";
import styled, { css } from "styled-components";
import tw from "twin.macro";
import { usePostValue } from "../..";
import { DP } from "../../../../common/types";
import createCustomContext from "../../../../context/customContext";
import { ProfileContext } from "../../../../context/profileContext";
import { UCancelButton, USaveButton, UTextarea } from "../../../../UI/UForma";
import { fetchPatchComment } from "../../api";
import { IComment } from "../../types";
import EditButton from "./components/EditButton";
import LikeArea from "./components/LikeArea";

interface Props extends DP {
  comment: IComment;
}

interface IContext {
  comment?: IComment;
  isEditing: boolean;
  isLoading: boolean;
  editingText?: string;
}

export const [useCommentValue, useComment, withContext] =
  createCustomContext<IContext>({
    isEditing: false,
    isLoading: false,
  });

const Comment = (props: Props) => {
  const profile = useContext(ProfileContext);

  const { set: setComment, setOne: setCommentOne, get: getComment } = useComment({
    comment: props.comment,
  });

  const isCommentsHidden = usePostValue((s) => s.isCommentsHidden);
  const currentEditingCommentId = usePostValue(
    (s) => s.currentEditingCommentId
  );
  const isEditing = useCommentValue((s) => s.isEditing);
  const editingText = useCommentValue((s) => s.editingText);
  const comment = useCommentValue((s) => s.comment);
  const isLoading = useCommentValue((s) => s.isLoading);

  const handleEditText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment({ editingText: e.target.value });
  };

  const handleCloseEditing = () => {
    setComment({ isEditing: false });
  };

  const handleSaveText = () => {
    if (!editingText.length) return;
    setComment({ isLoading: true });
    fetchPatchComment(comment._id, {
      comment: { text: editingText },
    })
      .then(({ data, status }) => {
				if (status == 200) {
          setComment({ comment: data.comment, isEditing: false });
        }
      })
      .finally(() => {
        setComment({ isLoading: false });
      });
  };

  useEffect(() => {
    if (isCommentsHidden && isEditing) {
      setComment({ isEditing: false });
    }
  }, [isCommentsHidden]);

  useEffect(() => {
    if (currentEditingCommentId != comment._id) {
      setComment({ isEditing: false });
    }
  }, [currentEditingCommentId]);

  return (
    <Wrapper className={props.className}>
      <UserPicture src={comment.author.picture} />
      <Username>{comment.author.username}</Username>
      <Text>
        {isEditing ? (
          <UTextarea value={editingText} onChange={handleEditText} />
        ) : (
          comment.text
        )}
      </Text>
      <Date>{dayjs(comment.createdAt).format("DD MMMM")}</Date>
      <ActionsWrapper>
        {profile?.user._id == comment.author._id && !isEditing && (
          <EditButton />
        )}
        {isEditing && (
          <>
            <UCancelButton onClick={handleCloseEditing}>Cancel</UCancelButton>
            <USaveButton disabled={isLoading} onClick={handleSaveText}>
              Save
            </USaveButton>
          </>
        )}
      </ActionsWrapper>
      <LikeArea />
    </Wrapper>
  );
};

const Wrapper = styled.div(() => [
  tw`text-[0.5rem] items-center overflow-auto space-x-1.5`,
  css`
    display: grid;
    grid-template:
      "userPic userName . likes" 0.8rem
      ". text text likes"
      ". date actions ."
      / auto 1fr auto auto;

    @media (hover: hover) {
      ${ActionsWrapper} > * {
        opacity: 0;
      }
      &:hover ${ActionsWrapper} > * {
        opacity: 0.3;
      }
      ${ActionsWrapper} > *:hover {
        opacity: 1;
      }
    }

    ${UserPicture} {
      grid-area: userPic;
    }
    ${Username} {
      grid-area: userName;
    }
    ${Text} {
      grid-area: text;
    }
    ${Date} {
      grid-area: date;
    }
    ${LikeArea} {
      grid-area: likes;
    }
    ${ActionsWrapper} {
      grid-area: actions;
    }
    ${UTextarea} {
			${tw`h-7`}
		}
  `,
]);

const UserPicture = styled.img(() => [
  tw`h-full object-cover aspect-square rounded-[50%]`,
]);

const Username = styled.div(() => [tw`text-[0.7em]`]);

const Text = styled.div(() => [tw`break-all grow text-[0.7em]`]);

const Date = styled.div(() => [tw`opacity-50 text-[0.3rem]`]);

const ActionsWrapper = styled.div(() => [
  tw`flex justify-end space-x-1 text-[0.3rem]`,
  css`
    span {
      letter-spacing: 1px;
    }
  `,
]);


export default styled(withContext(Comment))``;
